'use client'

import { type JSX, useEffect, useRef } from 'react'
import { preload } from 'react-dom'

// open is optional: an older CDN-cached embed.js (pre-S20-1089) installs mount/destroy only.
type Embed = { mount: (trigger?: Element) => void; destroy: () => void; open?: (prompt?: string) => void }

// Installed by embed.js itself (clients/ai-career-agent/embed-src/embed.ts, S20-1016); that declaration
// lives in another package, so this one mirrors it.
declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- only an interface merges into Window
  interface Window {
    SonicCareerGPT?: Embed
  }
}

const EMBED_SRC = 'https://ai-career-agent.sonicjobs.com/embed.js?clientId=6a29d0008821f186725dc648&minimize=0'

let loading: Promise<Embed | undefined> | undefined

/**
 * Inserts embed.js once per document. It installs `window.SonicCareerGPT` for the document's lifetime, so a
 * client return reuses it. A failed load clears the cache and its script, so the next visit retries.
 */
function loadEmbed(): Promise<Embed | undefined> {
  loading ??= new Promise<Embed | undefined>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = EMBED_SRC
    script.async = true
    // Undefined on an embed.js older than S20-1016, which has no mount()/destroy() and cannot be torn down.
    script.onload = (): void => resolve(window.SonicCareerGPT)
    script.onerror = (): void => {
      loading = undefined
      script.remove()
      reject(new Error('embed.js failed to load'))
    }
    document.head.append(script)
  })

  return loading
}

/**
 * The CareerGPT bar's trigger, and the owner of embed.js's lifecycle on /career-gpt: mounted on the way in,
 * destroyed on the way out, so links into and out of the page can be client navigations. An effect that
 * syncs with an external system; it does not touch our own DOM, it hands embed.js the trigger by ref.
 *
 * embed.js auto-mounts when it first runs, finding this same trigger, which makes the first `mount()` a
 * no-op; it also does so when the route was left while the script was still downloading, which is why a
 * resolved load for a component already gone destroys instead.
 */
export default function CareerGptEmbed({ className }: { className?: string }): JSX.Element {
  // During render, so it is in the server HTML and the download starts before hydration.
  preload(EMBED_SRC, { as: 'script' })
  const trigger = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = trigger.current ?? undefined
    let active = true
    loadEmbed()
      .then((embed) => (active ? embed?.mount(element) : embed?.destroy()))
      // No bar, as when the CDN is down today; the rejection has nothing left to act on.
      .catch(() => undefined)

    return (): void => {
      active = false
      window.SonicCareerGPT?.destroy()
    }
  }, [])

  return <div ref={trigger} data-career-gpt-trigger="bar" className={className} />
}
